import React from "react";
import { Box } from "@mui/material";
import { certificatePageSize } from "../../../domain/certificates/certificateTemplates";
import { CentredLayout, type CertificateDesign } from "./certificateParts";

/** How far in from the page edge the vine runs. */
const VINE_INSET = 40;
/** The length of one curve of the vine, and how far it swings to each side. */
const WAVE = 56;
const SWING = 7;
/** The vine stops this far short of each corner, where a flower sits. */
const CORNER_GAP = 28;

/** The fine rule inside the vine, clear of its swing, leaves and corner flowers. */
const RULE_INSET = VINE_INSET + 26;

type Point = { x: number; y: number };

/** The point a distance along the line from one point towards another. */
function pointToward(from: Point, to: Point, distance: number): Point {
  const length = Math.hypot(to.x - from.x, to.y - from.y);
  return {
    x: from.x + ((to.x - from.x) / length) * distance,
    y: from.y + ((to.y - from.y) / length) * distance,
  };
}

/** A point moved sideways off the vine's line. */
function offset(point: Point, direction: Point, distance: number): Point {
  return {
    x: point.x + direction.x * distance,
    y: point.y + direction.y * distance,
  };
}

/** A leaf pointing along +x from its stem, about 16px long. */
const LEAF = "M0,0 Q7,-6 16,0 Q7,6 0,0";

/**
 * A wavy vine along one edge, from one point to another: alternating curves,
 * each with a leaf at its outermost point, pointing onward and outward.
 */
function vineAlong(from: Point, to: Point) {
  const length = Math.hypot(to.x - from.x, to.y - from.y);
  const curves = Math.max(2, Math.round(length / WAVE));
  const step = length / curves;
  const along = pointToward(
    { x: 0, y: 0 },
    { x: to.x - from.x, y: to.y - from.y },
    1,
  );
  const across = { x: -along.y, y: along.x };
  const heading = (Math.atan2(along.y, along.x) * 180) / Math.PI;

  let path = `M${from.x},${from.y}`;
  const leaves: (Point & { angle: number })[] = [];
  for (let i = 0; i < curves; i += 1) {
    const side = i % 2 === 0 ? 1 : -1;
    const middle = pointToward(from, to, step * (i + 0.5));
    const end = pointToward(from, to, step * (i + 1));
    // A quadratic curve reaches half-way to its control point.
    const control = offset(middle, across, side * SWING * 2);
    path += ` Q${control.x},${control.y} ${end.x},${end.y}`;
    leaves.push({
      ...offset(middle, across, side * SWING),
      angle: heading + side * 45,
    });
  }
  return { path, leaves };
}

/** A small five-petal flower. */
const Flower: React.FC<
  Point & { petalColour: string; centreColour: string }
> = ({ x, y, petalColour, centreColour }) => (
  <g transform={`translate(${x} ${y})`}>
    {Array.from({ length: 5 }, (_, i) => {
      const angle = ((Math.PI * 2) / 5) * i - Math.PI / 2;
      return (
        <circle
          key={i}
          cx={8 * Math.cos(angle)}
          cy={8 * Math.sin(angle)}
          r={6.5}
          fill={petalColour}
          opacity={0.85}
        />
      );
    })}
    <circle r={4.5} fill={centreColour} />
  </g>
);

/** The vine around all four edges, with a flower at each corner. */
const VineBorder: React.FC<{
  width: number;
  height: number;
  vineColour: string;
  flowerColour: string;
}> = ({ width, height, vineColour, flowerColour }) => {
  const corners: Point[] = [
    { x: VINE_INSET, y: VINE_INSET },
    { x: width - VINE_INSET, y: VINE_INSET },
    { x: width - VINE_INSET, y: height - VINE_INSET },
    { x: VINE_INSET, y: height - VINE_INSET },
  ];
  const edges = corners.map((corner, i) => {
    const next = corners[(i + 1) % corners.length];
    return vineAlong(
      pointToward(corner, next, CORNER_GAP),
      pointToward(next, corner, CORNER_GAP),
    );
  });

  return (
    <Box
      component="svg"
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden="true"
      sx={{ position: "absolute", inset: 0, width, height }}
    >
      {edges.map((edge, i) => (
        <g key={i}>
          <path
            d={edge.path}
            fill="none"
            stroke={vineColour}
            strokeWidth={2}
            strokeLinecap="round"
          />
          {edge.leaves.map((leaf, j) => (
            <path
              key={j}
              d={LEAF}
              fill={vineColour}
              transform={`translate(${leaf.x} ${leaf.y}) rotate(${leaf.angle})`}
            />
          ))}
        </g>
      ))}
      {corners.map((corner, i) => (
        <Flower
          key={i}
          {...corner}
          petalColour={flowerColour}
          centreColour={vineColour}
        />
      ))}
    </Box>
  );
};

/**
 * Floral (stored as "elegant"): a light vine-and-leaf border with a flower at
 * each corner, and a fine rule inside it.
 */
export const elegantDesign: CertificateDesign = {
  bodyFont: "lora",
  Layout: ({ template, text, orientation }) => {
    const { width, height } = certificatePageSize(orientation);
    return (
      <CentredLayout
        template={template}
        text={text}
        orientation={orientation}
        inset={{ landscape: "92px 120px 76px", portrait: "100px 92px 84px" }}
        frame={
          <>
            <VineBorder
              width={width}
              height={height}
              vineColour={template.accentColour}
              flowerColour={template.primaryColour}
            />
            <Box
              sx={{
                position: "absolute",
                inset: RULE_INSET,
                border: `1px solid ${template.primaryColour}`,
              }}
            />
          </>
        }
      />
    );
  },
};
